import { useMutation } from '@tanstack/react-query';
import {
    EnumMessageLevel,
    type FilterExpression,
    resolveToBlobResponse,
    Result,
    useToasts,
    useSynqoHttp,
} from '@synqo/frontend-component-library';
import { useFileDownload } from '@Hooks/useFileDownload';
import { ApiExportRequest } from '@Model';

interface ExportPayload {
    exportOption: string;
    format: string;
    filter: FilterExpression | undefined;
}

export const useExport = (resourceBasePath: string) => {
    const { triggerDownload } = useFileDownload();
    const { post } = useSynqoHttp(resourceBasePath);
    const { pushAllToasts, pushToast } = useToasts();

    const { mutateAsync: download, isPending } = useMutation({
        mutationFn: async ({ exportOption, format, filter }: ExportPayload): Promise<Blob> => {
            const [blob, response] = await post<[Blob, Response], ApiExportRequest>(
                `/exports/${exportOption}/format/${format}`,
                {
                    filter_columns: filter,
                },
                undefined,
                resolveToBlobResponse,
            );

            if (!response.ok) {
                const errorText = await blob.text();
                try {
                    const errorData: Result<unknown> = JSON.parse(errorText);
                    pushAllToasts(errorData.messages);
                } catch {
                    pushToast({ message: 'Fehler beim Exportieren.', title: 'Export', level: EnumMessageLevel.Danger });
                }
                return blob;
            }

            triggerDownload(blob, response);
            return blob;
        },
    });

    return { download, isDownloading: isPending };
};
